import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { Spin } from 'antd';
import { Resizable } from 're-resizable';
import * as React from 'react';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import { Button } from '../ButtonV2/ButtonV2';
import { namedColors } from '../colors';
import { colorsV2 } from '../colors-v2';
import { FileUpload } from '../FileUpload';
import { QuestionV2 } from '../Question/QuestionV2';
import { showToast } from '../Toast';
import { ResizableImage, Spot, SPOT_DRAG_DROP_TYPE } from './types';

export interface SpotEditorProps {
  onAddSpot: (spot: Partial<Spot>) => void;
  onSpotChange: (spot: Partial<Spot>) => void;
  spots: Spot[];
  image: ResizableImage;
  onImageChange: (img: ResizableImage) => void;
  draggableSpot: React.ReactElement;
  droppedSpot: React.ReactElement;
}

const SpotEditorContext = React.createContext<SpotEditorProps>(null);

const SpotEditor = (props: SpotEditorProps) => {
  const [nextNumber, setNextNumber] = React.useState(0);

  const [uploading, setUploading] = React.useState(false);
  const { image, onImageChange, draggableSpot, droppedSpot } = props;

  if (!draggableSpot || !droppedSpot) {
    throw new Error('Please provide spot components');
  }

  return (
    <SpotEditorContext.Provider value={props}>
      <Container>
        {image ? <span>Drag {draggableSpot} to create new point</span> : null}

        <Spin spinning={uploading} tip="uploading...">
          <FileUpload
            onProgress={() => {
              setUploading(true);
            }}
            style={{ marginBottom: 16, marginTop: 16 }}
            labelBtn={image ? 'Replace' : 'Browse'}
            fileName={image?.name}
            fileInfo={{
              size: image?.size,
              uploadedDate: image?.uploadedDate,
            }}
            fileExt={image?.url.split('.').pop()}
            showUploadList={false}
            onSuccess={(res, originalFile) => {
              setUploading(false);
              const {
                tokenResponse: { downloadResourceUrl, path },
                file: { uploadedDate, size },
              } = res;
              // HACK: tell typescript silent
              const date = uploadedDate as any;
              const s = size as any;
              onImageChange({
                name: originalFile.name,
                url: downloadResourceUrl,
                path,
                uploadedDate: date,
                size: s,
              });
            }}
            onError={(err) => {
              setUploading(false);
              showToast({
                icon: 'danger',
                title: 'file upload failed',
                subTitle: err.message,
              });
            }}
          />
        </Spin>

        <DroppableImage
          onDrop={() => setNextNumber(nextNumber + 1)}
          onLoad={(e) => {
            const target = e.target as HTMLImageElement;
            const rect = target.getBoundingClientRect();
            onImageChange({ ...image, width: rect.width, height: rect.height });
          }}
        />
      </Container>
    </SpotEditorContext.Provider>
  );
};

const Container = styled.div``;

export default React.memo(SpotEditor);

// =================================================================================
// DraggableSpot
// =================================================================================

interface SpotItemProps extends React.HTMLAttributes<HTMLDivElement> {
  text: React.ReactNode;
}

export const SpotItem = React.forwardRef((props: SpotItemProps, ref: React.Ref<HTMLDivElement>) => {
  const { children, text, ...rest } = props;

  return (
    <SpotItemContainer ref={ref} {...rest}>
      <QuestionV2 no={text.toString()} noArrow marginRight={0} />
      {children}
    </SpotItemContainer>
  );
});

export const SpotItemWithBorder = React.forwardRef(
  (props: SpotItemProps, ref: React.Ref<HTMLDivElement>) => {
    const { children, text, ...rest } = props;

    return (
      <SpotItemContainer ref={ref} {...rest}>
        <SpotItemWrapper className="spot-item-wrapper">
          <SpotOrder className="order">{text}</SpotOrder>
          {children}
        </SpotItemWrapper>
      </SpotItemContainer>
    );
  }
);

export const SpotOrder = styled.span`
  display: inline-flex;
  width: 24px;
  min-width: 24px;
  height: 24px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: none;
  background-color: ${colorsV2.black80};
  font-size: 12px;
  color: ${colorsV2.white100};
  text-align: center;
  font-weight: 600;
`;

export const SpotPlaceholder = styled(SpotOrder)`
  border-radius: 100%;
  border: 1px dashed ${colorsV2.gray60};
  background-color: ${colorsV2.gray10};
  &.with-answer-text {
    margin: 5px 0 5px 4px;
  }
`;

export const SpotItemWrapper = styled.div`
  background: ${colorsV2.white100};
  border: 1px solid ${colorsV2.blue20};
  border-radius: 50%;
  padding: 3px;
  cursor: pointer;
  display: flex;
  &:hover {
    background-color: ${colorsV2.blue40};
    border-color: ${colorsV2.blue40};
  }
`;

export const CloseButton = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <Button.Icon type="secondary" size="medium" {...props}>
    <IconOtherClose />
  </Button.Icon>
);

export const SmallCloseButton = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <Button.Icon type="secondary" size="x-small" {...props}>
    <IconOtherClose />
  </Button.Icon>
);

export const SpotItemContainer = styled.div`
  display: inline-grid;
  gap: 4px;
  align-items: center;
  grid-auto-flow: column;
  transition: 300ms;
  padding: 6px 12px;

  &.no-border {
    border: none;
    padding: 0;
  }

  &.no-background {
    background: none;
  }

  .question-wrapper {
    .responsive-typography {
      min-width: 20px;
      text-align: center;
    }
  }
`;

// =================================================================================
// DroppableImage
// =================================================================================

interface DropItem {
  id: string;
}

interface DroppableImageProps extends React.HtmlHTMLAttributes<HTMLImageElement> {
  onDrop: () => void;
}

/**
 * For some reason, dropped position always take a way 1 pixel
 */
const APPROXIMATION = 1; // 1 pixel

const DroppableImage = (props: DroppableImageProps) => {
  const { onDrop, ...imageProps } = props;
  const {
    spots,
    onAddSpot: onAddBlank,
    onSpotChange,
    image,
    onImageChange,
    droppedSpot,
  } = React.useContext(SpotEditorContext);

  const imgRef = React.useRef(null);

  const [, drop] = useDrop({
    accept: SPOT_DRAG_DROP_TYPE,
    drop: (item: DropItem, monitor) => {
      const imgRect = imgRef.current.getBoundingClientRect();
      const droppedPosition = monitor.getSourceClientOffset();

      const spotX = droppedPosition.x - imgRect.x + APPROXIMATION;
      const spotY = droppedPosition.y - imgRect.y;

      const idx = spots.findIndex((spot) => spot.id === item.id);

      const changed = { x: spotX, y: spotY };

      if (idx > -1) {
        onSpotChange({ id: item.id, ...changed });
      } else {
        onAddBlank(changed);
      }
      onDrop();
    },
  });

  if (!image) return <ImgBorder />;

  const { width, height, url } = image;

  // TODO: resize -> move points
  return (
    <ImgBorder key={url}>
      {image ? (
        <Resizable
          style={{ margin: '0 auto', display: 'inline-block' }}
          maxWidth="100%"
          defaultSize={{ width: width || 'auto', height: height || 'auto' }}
          onResizeStop={(_, __, refToElement) => {
            const elmOffset = refToElement.getBoundingClientRect();
            onImageChange({ ...image, width: elmOffset.width, height: elmOffset.height });
          }}
        >
          <DropRegion ref={drop} className="drop-region">
            <Img
              loading="lazy"
              key={image.url}
              src={image.url}
              alt=""
              ref={imgRef}
              {...imageProps}
            />
            {spots.map((spot: Spot) => {
              // DANGEROUS: don't copy this without fully understand
              return React.cloneElement(droppedSpot, {
                key: spot.id,
                spot,
              });
            })}
          </DropRegion>
        </Resizable>
      ) : null}
    </ImgBorder>
  );
};

const DropRegion = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  display: inline-block;
`;

const ImgBorder = styled.div`
  background: ${namedColors.pureWhite};
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 14px;
  position: relative;
  margin-bottom: 16px;
  min-height: 136px;
  display: flex;
  justify-content: center;

  &::before {
    content: '';
    width: calc(100% + 32px);
    height: 2px;
    background-color: ${colorsV2.gray10};
    position: absolute;
    top: -1px;
    left: -16px;
  }

  &::after {
    content: '';
    width: calc(100% + 32px);
    height: 2px;
    background-color: ${colorsV2.gray10};
    position: absolute;
    bottom: -1px;
    left: -16px;
  }
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
`;
